/*
* This component is awfully similar to CreateAccountPost, but with some interesting use case differences.
*
* Namely, this component is planned to be used amongst other fields (and won't have it's own submit button), AND these fields
* don't have to be completed. As a result, it will look to this.props.showValidation to determine if errors should actually be shown (if the user clicks
* some other submit with invalid details, for example). This component keeps track of what is wrong, the consumer of the component determines if it's a good time to show the errors.
*
* Also, because it's not (always ** read last paragraph **) necessary for the consumer to worry about this component's validation (if a user chooses not to create an account),
* this component will call this.props.handleCreateAccountCheckbox with a boolean designating the user's intent to do so and that the consumer must now
* wait for valid password info before proceeding.
*
* Lastly, this component will assess the validation of the content in its fields every time state or props are updated.
* If all of the content is valid at any point it will fire this.props.handleValidInfo() which will allow the consumer to know
* the chosen password — and implicitly — that this part of the form is completed. (We'll also call resetValidInfo() if this is ever made untrue again.)
*
* If this component is used with 'mandatory' prop set to 'true', the optional checkbox will be hidden, and the password inputs will be visible always.
* Additionally, the this.props.handleCreateAccountCheckbox will not be called, because the consumer should assume that they should wait for
* this.props.handleValidInfo() to show that the user successfully completed the, now mandatory, password selection.
* */

import PropTypes from 'prop-types';

import React from 'react';
import { StyleSheet, css } from 'aphrodite';
import classnames from 'classnames';
import passwordValidator from './util/passwordValidator';
import { TextInput, Checkbox, HeadingText, CaptionText, DropdownMenu } from '@paypalcorp/pp-react';
import NationalId from './NationalId';
import { getCountryInfo, getFormattedDob, validateDob, validateDobIs18 } from './util/localizer';
import * as FPT from '../utility/fpt';
import isEmpty from 'lodash/isEmpty';

const styles = StyleSheet.create({
	mainTitle: {
		marginBottom: '10px'
	},
	mainBlurb: {
		marginBottom: '15px'
	},
	itsFreeLine: {
		margin: '30px 0 15px 0',
	},
	shiftRight: {
		marginLeft: '40px',
	},
	hide: {
		display: 'none',
	},
	createAccountCheckboxWrapper: {
		position: 'absolute',
		width: 'inherit',
		display: 'inline',
	},
	createAccountCheckbox: {
		':before': {
			overflow: 'hidden',
			border: '1px solid #9b9b9b',
		}
	},
	createAccountLine: {
		marginLeft: '40px'
	},
	passwordWarningWrapper: {
		background: '#fff7f7',
		padding: '10px 10px 5px',
		borderRadius: 5,
		border: '1px solid #c72e2e',
		marginTop: 15,
	},
	passwordHintWrapper: {
		background: '#eef1f6',
		padding: 5,
		paddingLeft: 40,
		borderRadius: 5,
		marginTop: 5,
		fontSize: 12,
	},
	passwordWarningItem: {
		margin: 0,
		paddingBottom: 5,
	},
	legalCheckboxWrapper: {
		position: 'absolute',
		width: 'inherit',
		display: 'inline',
	},
	legalCheckbox: {
		':before': {
			overflow: 'hidden',
			border: '1px solid #9b9b9b',
		}
	},
	redBorder: {
		':before': {
			background: '#fff7f7',
			border: '1px solid #c72e2e',
		}
	},
	legalBlurb: {
		lineHeight: '18px',
		marginLeft: '40px'
	},
	legalTextOnly: {
		lineHeight: '18px'
	},
	button: {
		display: 'block',
		margin: '40px auto 0'
	},
	passwordWrapper: {
		marginBottom: '10px'
	},
	dobWrapper: {
		marginBottom: '10px'
	},
	dobHelpText: {
		border: 'solid 1px #0093e0',
		background: '#f2fafe',
		color: 'black',
		borderTop: 'none',
	},
	dropdown: {
		padding: 0,
		textIndent: 12,
		boxSizing: 'content-box',
		appearance: 'none'
	},
	noHelperText: {
		marginTop: '.9375rem'
	}
});

class CreateAccountPre extends React.Component {
	static propTypes = {
		mainTitle: PropTypes.string.isRequired,
		mainBlurb: PropTypes.string.isRequired,
		legalBlurbHTML: PropTypes.object.isRequired,
		createAccountCheckboxContent: PropTypes.string.isRequired,
		handleValidInfo: PropTypes.func.isRequired,
		resetValidInfo: PropTypes.func.isRequired,

		handleAllValidInfo: PropTypes.func,
		resetAllValidInfo: PropTypes.func,

		legalOnlyHTML: PropTypes.object,
		labelnationality: PropTypes.string,
		consentBlurbHTML: PropTypes.object,
		supportedCountries: PropTypes.array,
		occupations: PropTypes.array,

		showValidationErrors: PropTypes.bool.isRequired,
		itsFreeLine: PropTypes.string,
		createAccountHelp1: PropTypes.string,
		createAccountHelp2: PropTypes.string,
		labeldob: PropTypes.string,
		helpdob: PropTypes.string,
		dobPattern: PropTypes.string,
		dobHelp: PropTypes.string,
		occupationError: PropTypes.bool,
		nationalityError: PropTypes.bool,
		defaultIdType: PropTypes.string,
		isNNA: PropTypes.bool,

		label1: PropTypes.string,
		handleFocusOnPasswordOne: PropTypes.func,
		label2: PropTypes.string,
		handleCreateAccountCheckbox: PropTypes.func,
		showCreateAccountFields: PropTypes.bool,
		mandatory: PropTypes.bool,
		country: PropTypes.string,
		labelocc: PropTypes.string,
		passwordWarnings: PropTypes.shape({
			_MISMATCH: PropTypes.string.isRequired,
			_IS_NULL: PropTypes.string.isRequired,
			_TOO_SHORT: PropTypes.string.isRequired,
			_TOO_LONG: PropTypes.string.isRequired,
			_CONTAINS_REPEATED_CHARACTERS: PropTypes.string.isRequired,
			_CONTAINS_NUMBER_SEQUENCE: PropTypes.string.isRequired,
			_CONTAINS_LETTER_SEQUENCE: PropTypes.string.isRequired,
			_CONTAINS_NUMBER_LETTER_SEQUENCE: PropTypes.string.isRequired,
			_IS_BLACKLISTED: PropTypes.string.isRequired,
			_CONTAINS_SPACES: PropTypes.string.isRequired,
			_HAS_ILLEGAL_CHARACTERS: PropTypes.string.isRequired,
			_LEGAL_CHECKBOX: PropTypes.string.isRequired,
			_INVALID_DOB: PropTypes.string,
			_REQUIRED_DOB: PropTypes.string,
			_HELP_DOB: PropTypes.string,
			_MUST_18_DOB: PropTypes.string,
			_OCCUPATION: PropTypes.string,
			_NATIONALITY: PropTypes.string,
			_NATIONAL_ID_TYPE: PropTypes.string,
			_NATIONAL_ID_NUMBER: PropTypes.string,
			_HINT1: PropTypes.string,
			_HINT2: PropTypes.string,
			_HINT3: PropTypes.string,
		}).isRequired,
		incentive: PropTypes.object,
	};

	constructor(props) {
		super(props);
		const { country = 'US', defaultIdType } = props;
		const countryInfo = getCountryInfo(country);

		this.state = {
			showCreateAccountFields: false,
			password1: '',
			password2: '',
			dateOfBirth: '', // For cross-border
			occupationIdx: -1, // For cross-border
			nationalityIdx: -1,
			nationalIdNumber: '',
			nationalIdType: defaultIdType || '',
			acceptedLegal: false, // For cross-border
			consentCommunication: countryInfo.consentCheckBoxEnabled || false, // For cross-border
			showPasswordHint: false, // For cross-border
			showDateOfBirthHelp: false, // For Date of Birth
			passwordErrors: [],
			optional: false
		};
	}

	// every time the component updates, should check for valid info to fire to parent
	componentDidUpdate(prevProps, prevState) {
		// If someone where calling setState (or anything prompting rerender) within this.props.handleValidInfo or this.props.resetValidInfo
		// it would cause an infinite loop (component would rerender, passwords would still be valid, etc.).
		// This outer `if` statement checks that the password or checkbox has changed before calling the parent method.
		if (prevState.password1 !== this.state.password1 ||
			prevState.password2 !== this.state.password2 ||
			prevState.acceptedLegal !== this.state.acceptedLegal) {
			const passwordErrors = passwordValidator(this.state.password1);
			// if the passwords match, have no error, and the user has accepted the legal agreement...
			if (this.doPasswordsMatch() && !passwordErrors.length && this.state.acceptedLegal) {
				// call the prop function that signifies to the parent that everything is ready to go!
				this.props.handleValidInfo(this.state.password1);
			} else {
				// This covers the scenario where a user has valid information, but goes back and changes things to be invalid later
				// consumers need to know to clear the initial valid information from their stores.
				this.props.resetValidInfo();
			}
		}

		// This outer `if` statement checks that the password or acceptedLegal or dateOfBirth or occupation or nationalityor checkbox has changed before calling the parent method.
		if (prevState.password1 !== this.state.password1 ||
			prevState.password2 !== this.state.password2 ||
			prevState.acceptedLegal !== this.state.acceptedLegal ||
			prevState.dateOfBirth !== this.state.dateOfBirth ||
			prevState.occupationIdx !== this.state.occupationIdx ||
			prevState.nationalityIdx !== this.state.nationalityIdx ||
			prevState.nationalIdType !== this.state.nationalIdType ||
			prevState.nationalIdNumber !== this.state.nationalIdNumber ||
			prevState.consentCommunication !== this.state.consentCommunication) {

			let { passwordValid = true, dobValid = true, occValid = true, legalValid = true, natValid = true, naIdTypeValid = true, naIdNoValid = true, } = {};

			const { country = 'US' } = this.props;
			const { password1, acceptedLegal, dateOfBirth, occupationIdx, nationalityIdx, nationalIdType, nationalIdNumber, consentCommunication, } = this.state;
			const passwordErrors = passwordValidator(password1);
			const countryInfo = getCountryInfo(country);

			let userData = { password: password1 };
			passwordValid = this.doPasswordsMatch() && !passwordErrors.length;

			if (countryInfo.showLegalCheckBox) {
				userData.legalValid = acceptedLegal;
				legalValid = !!acceptedLegal;
			}

			if (countryInfo.showDOB) {
				userData.dateOfBirth = dateOfBirth;
				userData.dateOfBirthPattern = this.getDobPattern();
				dobValid = validateDob(dateOfBirth, userData.dateOfBirthPattern);
			}

			if (countryInfo.showOccupation) {
				const occupations = this.props.occupations || countryInfo.occupations;
				let occupation = occupationIdx === -1 ? '' : occupations[occupationIdx].value;
				userData.occupation = occupation;
				occValid = !!occupation;
			}

			if (countryInfo.showNationality) {
				let nationality = nationalityIdx !== -1 ? this.props.supportedCountries[nationalityIdx].countryCode : '';
				userData.nationality = nationality;
				natValid = !!nationality;
			}

			if (countryInfo.showNationalId) {
				userData.nationalIdType = nationalIdType;
				naIdTypeValid = !!nationalIdType;
			}

			if (countryInfo.showNationalId) {
				userData.nationalIdNumber = nationalIdNumber;
				naIdNoValid = !!nationalIdNumber;
			}

			if (countryInfo.showConsentCheckBox) {
				userData.consentCommunication = consentCommunication;
			}

			if (passwordValid && legalValid && dobValid && occValid && natValid && naIdTypeValid && naIdNoValid) {
				// If passwords are identical, have no errors, and legal has
				// been accepted. Call the prop function that signifies to the
				// parent that everything is ready to go!
				if (this.props.handleAllValidInfo) {
					this.props.handleAllValidInfo(userData);
				}
			} else if (this.props.resetAllValidInfo) {
				// This covers the scenario where a user has valid information,
				// but goes back and changes things to be invalid later
				// consumers need to know to clear the initial valid information
				// from their stores.
				this.props.resetAllValidInfo(userData);
			}
		}

		if (prevProps.showValidationErrors !== this.props.showValidationErrors &&
			this.props.showValidationErrors &&
			!isEmpty(this.state.passwordErrors)) {
			FPT.trackFieldError(
				'password',
				'PASSWORD_VALIDATION_ERROR',
				this.state.passwordErrors.join(',')
			);
		}
	}

	UNSAFE_componentWillReceiveProps(nextProps) {
		// in the case that the user fills nothing out after ticking the create account box
		// we need to show password validation errors for empty password fields
		this.setState({
			passwordErrors: passwordValidator(this.state.password1)
		});

		// Set optional to false when its not mandatory
		// This is required for re-setting "optional" when it changes
		// to mandatory from optional
		if (this.props.mandatory) {
			this.setState({
				optional: false
			});
		}
	}

	handleCreateAccountCheckbox = (event) => {
		this.props.handleCreateAccountCheckbox(event.target.checked);
		this.setState({
			optional: event.target.checked,
			showCreateAccountFields: event.target.checked
		});
	};

	handleUpdatePasswords = (password, whichInput, hasPasswordHint) => {

		let newState = {
			[whichInput]: password,
			showPasswordHint: hasPasswordHint, // we only show password hint for password1 if is enabled
			// we only base the password validation on 'input1', otherwise things get really confusing
			passwordErrors: (whichInput === 'password1') ? passwordValidator(password) : this.state.passwordErrors,
		};
		this.setState(newState);
		return password; // returning for TextInput https://github.paypal.com/react/react-text-input
	};

	hidePasswordHint = (isHint) => {
		if (isHint) {
			this.setState({ showPasswordHint: false });
		}
	};

	handleLegalCheckbox = (event) => {
		this.setState({
			acceptedLegal: event.target.checked
		});
	};

	handleConsentCheckbox = (event) => {
		this.setState({
			consentCommunication: event.target.checked
		});
	};

	handleResetPasswordFields = () => {
		this.setState({
			optional: false,
			acceptedLegal: false,
			showCreateAccountFields: false,
		});
		this.handleUpdatePasswords('', 'password1');
		this.handleUpdatePasswords('', 'password2');
	}

	/**
 * check that the two passwords within state are matching
 * @return {boolean}
 */
	doPasswordsMatch = () => {
		return this.state.password1 === this.state.password2;
	};

	/**
 * Return dobPattern from specified props or get it from countryInfo
 */
	getDobPattern = () => {
		if (this.props.dobPattern) {
			return this.props.dobPattern;
		}

		const { country = 'US' } = this.props;
		const countryInfo = getCountryInfo(country);

		return countryInfo.DOBPattern;

	};

	/**
 * check if date of birth is valid
 * @return {string}
 */
	handleChangeDob = (date) => {
		let dateOfBirth = getFormattedDob(date, this.getDobPattern());

		this.setState({
			dateOfBirth: dateOfBirth,
			dobReqErrors: date === '',
			dobErrors: date !== '' && !validateDob(dateOfBirth, this.getDobPattern())
		});
		return dateOfBirth;
	};

	validateDobIs18 = (date) => {
		this.setState({
			dobIs18Errors: !validateDobIs18(date, this.getDobPattern())
		});
	};

	handleDobBlur = (date) => {
		this.setState({ dobReqErrors: date === '', showDateOfBirthHelp: false });
		this.validateDobIs18(date);
	};

	handleDobFocus = (date) => {
		this.setState({ dobReqErrors: false, showDateOfBirthHelp: true });
	};

	handleChangeOccupation = (index) => {
		this.setState({ occupationError: index === -1, occupationIdx: index });
	};

	handleChangeNationality = (index) => {
		this.setState({ nationalityError: index === -1, nationalityIdx: index });
	};

	renderPasswordErrors = () => {
		return (
			<span>
				{/* password mismatch content */}
				{!this.doPasswordsMatch() && <p className={classnames('test_passwordMessage', css(styles.passwordWarningItem))}>{this.props.passwordWarnings._MISMATCH}</p>}
				{/* password formatting content */}
				{this.state.passwordErrors.map(code => {
					const warningMessage = this.props.passwordWarnings[code];
					return (
						<p key={code} className={classnames('test_passwordMessage', css(styles.passwordWarningItem))}>{warningMessage}</p>
					);
				})}
			</span>
		);
	};

	handleNationalIdType = (nationalIdType) => {
		this.setState({ nationalIdType });
	};

	handleNationalIdNumber = (nationalIdNumber) => {
		this.setState({ nationalIdNumber });
	};

	resetNationalId = () => {
		this.setState({ nationalIdType: null, nationalIdNumber: null });
	};

	getOccupationOptions = (occupations) => {
		return occupations.map((item) => {
			return {
				primaryText: item.label,
			}
		})
	}

	getNationalityOptions = (supportedCountries) => {
		return supportedCountries.map((country) => {
			return {
				primaryText: country.countryName,
			}
		})
	}

	render() {
		const { acceptedLegal, passwordErrors, dateOfBirth, occupationIdx, nationalityIdx, nationalIdType, nationalIdNumber, dobReqErrors, dobErrors, dobIs18Errors, occupationError, nationalityError, showPasswordHint, showDateOfBirthHelp } = this.state;
		const { showValidationErrors, country = 'US', supportedCountries, defaultIdType = '' } = this.props;
		const countryInfo = getCountryInfo(country);
		const dateOfBirthHelp = showDateOfBirthHelp && countryInfo.showDOBHelp;
		const legalCheckBoxErrorsExist = countryInfo.showLegalCheckBox && !acceptedLegal;
		const passwordErrorsExist = !this.doPasswordsMatch() || !!passwordErrors.length;
		const occupationErrorsExist = showValidationErrors && countryInfo.showOccupation && (occupationError || occupationIdx === -1);
		const nationalityErrorsExist = showValidationErrors && countryInfo.showNationality && (nationalityError || nationalityIdx === -1);
		const dateOfBirthErrorsExist = showValidationErrors && countryInfo.showDOB && !dateOfBirthHelp && (dobErrors || dobReqErrors || !dateOfBirth);
		const dateOfBirthIs18ErrorsExist = showValidationErrors && countryInfo.showDOBMust18 && dobIs18Errors && !dateOfBirthHelp;
		const nationalIdTypeErrorsExist = showValidationErrors && countryInfo.showNationalId && !nationalIdType;
		const nationalIdNoErrorsExist = showValidationErrors && countryInfo.showNationalId && !nationalIdNumber;
		const shouldShowErrorMessages = showValidationErrors && (passwordErrorsExist || legalCheckBoxErrorsExist || occupationErrorsExist || nationalityErrorsExist || dateOfBirthErrorsExist || dateOfBirthIs18ErrorsExist || nationalIdTypeErrorsExist || nationalIdNoErrorsExist);
		const occupations = this.props.occupations || countryInfo.occupations;
		const isCountryUS = country === 'US';
		const createAccountCheckboxContent = isCountryUS ? this.props.incentive?.createAccountCheckboxContent : this.props.createAccountCheckboxContent;

		return (
			<div>
				{this.props.isNNA ? (
					<div className="nnaTitle">
						<HeadingText size={this.props.isNNA ? 'sm' : 'md'}>
							{this.props.mainTitle}
						</HeadingText>
						<img
							src="https://www.paypalobjects.com/donate/donate_nna_wallet.svg"
							alt=""
						/>
					</div>
				) : (
					<>
						<HeadingText size="md">{this.props.mainTitle}</HeadingText>
						<CaptionText className={css(styles.mainBlurb)}>{this.props.mainBlurb}</CaptionText>
					</>
				)}


				{!this.props.mandatory && // if the password is optional — show the optional checkbox
					<span>
						{!this.props.isNNA ? (
							<p className={`${css(styles.itsFreeLine)} vx_text-5`}>
								{this.props.itsFreeLine}
							</p>
						) : null}
						<div className="ppvx_checkbox">
							<Checkbox
								label={createAccountCheckboxContent}
								name="createAccountCheckbox"
								id="createAccountCheckbox"
								onChange={this.handleCreateAccountCheckbox}
							/>
						</div>
					</span>
				}

				<div className={css(!this.props.mandatory && !this.props.isNNA && !isCountryUS && styles.shiftRight, !this.props.mandatory && !this.state.optional && styles.hide)}>
					{!this.props.mandatory && !this.props.isNNA && !isCountryUS &&
						<div>
							{this.props.createAccountHelp1 && <p className="vx_text-5">{this.props.createAccountHelp1}</p>}
							{this.props.createAccountHelp2 && <p className="vx_text-5">{this.props.createAccountHelp2}</p>}
						</div>
					}
					<div className={classnames(css(styles.passwordWrapper), css(isCountryUS && styles.noHelperText),{
                        'ppvx_field--error': showValidationErrors && passwordErrorsExist
                    })}>
						<TextInput
							label={this.props.label1}
							name="password1"
							type="password"
							id="password1"
							onMouseOut={() => this.hidePasswordHint(countryInfo.showPasswordHint)}
							onBlur={() => this.hidePasswordHint(countryInfo.showPasswordHint)}
							onChange={(event) => this.handleUpdatePasswords(event.target.value, 'password1', countryInfo.showPasswordHint)}
							onFocus={this.props.handleFocusOnPasswordOne}
							value={this.state.password1}
						/>
						{
							showPasswordHint && countryInfo.showPasswordHint &&
							<div id="password_hint" className={`ppvx_alert ppvx_alert--type_attention ${css(styles.passwordHintWrapper)}`}>
								{this.props.passwordWarnings._HINT1 && <div>{this.props.passwordWarnings._HINT1}</div>}
								{this.props.passwordWarnings._HINT2 && <div>{this.props.passwordWarnings._HINT2}</div>}
								{this.props.passwordWarnings._HINT3 && <div>{this.props.passwordWarnings._HINT3}</div>}
							</div>
						}
					</div>
					<div className={classnames(css(styles.passwordWrapper), {
						'ppvx_field--error': showValidationErrors && passwordErrorsExist
					})}>
						<TextInput
							label={this.props.label2}
							name="password2"
							type="password"
							id="password2"
							onChange={(event) => this.handleUpdatePasswords(event.target.value, 'password2')}
							value={this.state.password2}
						/>
					</div>
					{
						countryInfo.showDOB &&
						<div className={classnames(css(styles.dobWrapper),
							{
								'ppvx_field--error': showValidationErrors && dateOfBirthErrorsExist
							})}>
							<TextInput type="tel"
								id="dateofbirth"
								name="dateofbirth"
								value={this.state.dateOfBirth}
								className="vx_form-control vx_form-control_complex"
								label={this.props.labeldob}
								autoComplete="off"
								placeholder={this.props.dobHelp}
								onChange={(event) => this.handleChangeDob(event.target.value)}
								onFocus={(event) => this.handleDobFocus(event.target.value)}
								onBlur={(event) => this.handleDobBlur(event.target.value)}
								helperText={dateOfBirthHelp && this.props.passwordWarnings._HELP_DOB}
							/>
						</div>
					}
					{
						countryInfo.showOccupation &&
						<div className={classnames({
							'ppvx_field--error': showValidationErrors && occupationErrorsExist
						})}
							style={{ paddingTop: 0, margin: '10px 0 0', position: 'relative' }}>
							<DropdownMenu type="button"
								id="occupation"
								name="occupation"
								selectedItemIndex={this.state.occupationIdx}
								onSelection={this.handleChangeOccupation}
								label={this.props.labelocc}
								placeholder=""
								options={this.getOccupationOptions(occupations)}
							/>
						</div>
					}
					{
						countryInfo.showNationality &&
						<div className={classnames({
							'ppvx_field--error': showValidationErrors && nationalityErrorsExist
						})}
							style={{ paddingTop: 0, margin: '10px 0 0', position: 'relative' }}>
							<DropdownMenu type="button"
								id="nationality"
								name="nationality"
								selectedItemIndex={nationalityIdx}
								onSelection={this.handleChangeNationality}
								label={this.props.labelnationality}
								placeholder=""
								options={this.getNationalityOptions(supportedCountries)}
							/>
						</div>
					}
					{
						countryInfo.showNationalId &&
						<div className={classnames(css(styles.passwordWrapper), {
							'ppvx_field--error': showValidationErrors && passwordErrorsExist
						})}>
							<NationalId
								validNationalIdType={this.handleNationalIdType}
								validNationalIdNumber={this.handleNationalIdNumber}
								resetNationalId={this.resetNationalId}
								defaultIdType={defaultIdType}
								{...this.props}
							/>
						</div>
					}
					{
						shouldShowErrorMessages &&
						<div className={css(styles.passwordWarningWrapper)}>
							{showValidationErrors && passwordErrorsExist && this.renderPasswordErrors()}
							{showValidationErrors && countryInfo.showDOB && dobErrors && !dateOfBirthHelp &&
								<div><p className={classnames('test_legalMessage', css(styles.passwordWarningItem))}>
									{this.props.passwordWarnings._INVALID_DOB}
								</p></div>}
							{showValidationErrors && countryInfo.showDOB && (dobReqErrors || !dateOfBirth) && !dateOfBirthHelp &&
								<div><p className={classnames('test_legalMessage', css(styles.passwordWarningItem))}>
									{this.props.passwordWarnings._REQUIRED_DOB}
								</p></div>}
							{dateOfBirthIs18ErrorsExist &&
								<div><p className={classnames('test_legalMessage', css(styles.passwordWarningItem))}>
									{this.props.passwordWarnings._MUST_18_DOB}
								</p></div>}
							{occupationErrorsExist &&
								<div><p className={classnames('test_occupationMessage', css(styles.passwordWarningItem))}>
									{this.props.passwordWarnings._OCCUPATION}
								</p></div>}
							{nationalityErrorsExist &&
								<div><p className={classnames('test_occupationMessage', css(styles.passwordWarningItem))}>
									{this.props.passwordWarnings._NATIONALITY}
								</p></div>}
							{nationalIdTypeErrorsExist &&
								<div><p className={classnames('test_occupationMessage', css(styles.passwordWarningItem))}>
									{this.props.passwordWarnings._NATIONAL_ID_TYPE}
								</p></div>}
							{nationalIdNoErrorsExist &&
								<div><p className={classnames('test_occupationMessage', css(styles.passwordWarningItem))}>
									{this.props.passwordWarnings._NATIONAL_ID_NUMBER}
								</p></div>}
							{showValidationErrors && countryInfo.showLegalCheckBox && !acceptedLegal &&
								<div><p className={classnames('test_legalMessage', css(styles.passwordWarningItem))}>
									{this.props.passwordWarnings._LEGAL_CHECKBOX}
								</p></div>}
						</div>
					}

					{countryInfo.showConsentCheckBox &&
						<div className="legalWrapper">
							<Checkbox
								id="consentCheckbox"
								checked={this.state.consentCommunication}
								onChange={this.handleConsentCheckbox}
								className="legal-text"
								label={this.props.consentBlurbHTML}
							/>
						</div>
					}

					{countryInfo.showLegalCheckBox && <div className="legalWrapper">
						<Checkbox
							id="accountLegalCheckbox"
							name="accountLegalCheckbox"
							checked={this.state.acceptedLegal}
							onChange={this.handleLegalCheckbox}
							className="legal-text"
							label={this.props.legalBlurbHTML}
							error={showValidationErrors && !acceptedLegal}
						/>
					</div>}

					{countryInfo.showLegalTextOnly &&
						<div className="legalWrapper">
							<CaptionText id="legalTextOnly" className="legal-text">{this.props.legalOnlyHTML}</CaptionText>
						</div>
					}
				</div>
			</div>
		);
	}
}

export default CreateAccountPre;
